import type {AnyQueryRoute, RouteMatchData} from '@github-ui/react-core/future/query-route'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {hashKey, useQueryClient} from '@github-ui/react-query'
import {useRouteLoaderData} from '@github-ui/react-router'
import type {UpdateSavedViewResponse, UpdateSavedViewVariables} from '@github-ui/saved-views'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useLayoutEffect, useRef, useState} from 'react'

import {isViewConfiguration} from '../components/board/issues/config-payload'
import {getConfigurationDirtyState, type ViewConfiguration} from '../contexts/views/view-configuration'
import {canonicalQueryKey, copyRouteQueryData, requestPathname} from '../helpers/saved-view-route-cache'
import {restoreSavedViewOverrides, SAVED_VIEW_URL_PARAMS} from '../utils/saved-view-url-params'
import {useLoadingNavigation} from './use-saved-view-navigation'
import type {SavedViewRequestSearchParams} from './use-saved-view-prefetch'

/** The search-param reader for each route query whose response Save moves to the clean URL, by query name. */
export type SavedViewRouteQueryReaders<Route extends AnyQueryRoute> = {mainQuery: SavedViewRequestSearchParams} & {
  [QueryName in Exclude<keyof Route['queries'], 'mainQuery'>]?: SavedViewRequestSearchParams
}

type RouteQueries = Partial<Record<string, {queryKey: RouteQueryKey}>>

interface RouteQueryRequest {
  queryName: string
  /** The key the route loaded the query with. */
  from: RouteQueryKey
  /** The key the same query has at a URL with `searchParams`. */
  keyFor: (searchParams: URLSearchParams) => RouteQueryKey
}

function routeQueryRequests(
  queries: RouteQueries | undefined,
  requestSearchParams: Partial<Record<string, SavedViewRequestSearchParams>>,
): RouteQueryRequest[] {
  const requests: RouteQueryRequest[] = []
  for (const [queryName, read] of Object.entries(requestSearchParams)) {
    const from = queries?.[queryName]?.queryKey
    const pathname = from && requestPathname(from)
    if (!from || !read || pathname === undefined) continue
    requests.push({queryName, from, keyFor: searchParams => canonicalQueryKey(from, pathname, read(searchParams))})
  }
  return requests
}

interface UseClearSavedViewOverridesInput<Route extends AnyQueryRoute> {
  /** The route that serves the view. */
  route: Route
  /** Reads the request each listed query sends for a URL, the same way its `queryDeps` does. */
  requestSearchParams: SavedViewRouteQueryReaders<Route>
  /** Removes the prefetches for the view except `promoted`. Usually from `useEvictSavedViewPrefetches`. */
  evictPrefetches: (promoted?: RouteQueryKey) => void
  /** The configuration on screen, with a pick still loading. Absent when the view has no options. */
  configuration?: ViewConfiguration
}

interface SavedViewOverrideHandlers {
  /** Handler that is called with the response and the request when Save succeeds. */
  onSaveSuccess: (
    response: UpdateSavedViewResponse<ViewConfiguration>,
    sent: UpdateSavedViewVariables<ViewConfiguration>,
  ) => void
  /**
   * Handler that is called when an edit to the view is saved. `servedQuery` is the query the
   * response on screen answered for. Returns whether the page shows or reloads what the edit saved,
   * so the caller refreshes otherwise.
   */
  onEditSuccess: (
    response: UpdateSavedViewResponse<ViewConfiguration>,
    sent: UpdateSavedViewVariables<ViewConfiguration>,
    servedQuery: string,
  ) => boolean
  /** Handler that is called when the view's edit form opens, to note the configuration on screen. */
  onEditStart: () => void
  /** Handler that is called when the edit form is cancelled, which puts back what `onEditStart` noted. */
  onEditCancel: () => void
  /** Whether `configuration` differs from when `onEditStart` was called, on the same view. */
  editConfigurationChanged: boolean
}

interface EditStart {
  pathname: string
  search: URLSearchParams
  configuration: ViewConfiguration | undefined
}

// The repository page keeps its edit form open across a move to another view.
function staysOnView(pathname: string, {current, destination}: ReturnType<typeof useLoadingNavigation>): boolean {
  return current.pathname === pathname && (destination ?? current).pathname === pathname
}

/**
 * Returns the handlers a saved view page calls once the view is saved or edited.
 *
 * After Save, the page moves to its URL without the parameters Save made redundant: `q` and
 * `page`, plus the configuration parameters when Save sent a configuration. Other parameters
 * stay, and the hash goes.
 *
 * Before it navigates, each query in `requestSearchParams` has its response on screen copied to
 * the key the new URL loads, with the saved configuration and view on `mainQuery`, so the page
 * keeps what it shows without a request. A query whose request differs at the new URL, such as
 * a list on page two, is not copied and loads again. Prefetches for the view are cleared,
 * keeping the copied `mainQuery` response.
 *
 * After an edit that kept the query on screen, its responses are copied the same way, with the saved
 * configuration. After one that changed it, responses cached for the URL the page moves to are
 * dropped rather than drawn as the view was before, and the page reloads. Prefetches for the view
 * are cleared, and a previewed `q` and its `page` leave the URL, along with the configuration
 * parameters when the edit saved the configuration and no pick is loading. When that leaves the
 * route unreloaded, its queries refetch instead. When a navigation on the view is loading, such
 * as an option pick, the page moves to that URL without `q`, so the pick stays. A loading
 * navigation to another page is left to finish. The edit handler reads the navigation and the
 * route queries when it is called, since the edit mutation keeps them from the render where Save
 * was clicked.
 *
 * Cancelling an edit puts back the configuration parameters the URL had when the edit form opened.
 *
 * Needs a data router.
 */
export function useClearSavedViewOverrides<Route extends AnyQueryRoute>({
  route,
  requestSearchParams,
  evictPrefetches,
  configuration: shownConfiguration,
}: UseClearSavedViewOverridesInput<Route>): SavedViewOverrideHandlers {
  const navigate = useNavigate()
  const navigation = useLoadingNavigation()
  const {pathname, search} = navigation.current
  const queryClient = useQueryClient()
  const queries: RouteQueries | undefined = useRouteLoaderData<RouteMatchData<Route> | undefined>(route.id)?.queries
  const navigationRef = useRef(navigation)
  const queriesRef = useRef(queries)
  useLayoutEffect(() => {
    navigationRef.current = navigation
    queriesRef.current = queries
  })

  const onSaveSuccess = useCallback(
    (response: UpdateSavedViewResponse<ViewConfiguration>, sent: UpdateSavedViewVariables<ViewConfiguration>) => {
      const savedConfiguration =
        sent.configuration !== undefined && isViewConfiguration(response.configuration)
          ? response.configuration
          : undefined
      // The URL that now loads the response on screen.
      const unchanged = new URLSearchParams(search)
      unchanged.delete('q')
      if (savedConfiguration) {
        for (const param of SAVED_VIEW_URL_PARAMS) unchanged.delete(param)
      }
      const destination = new URLSearchParams(unchanged)
      destination.delete('page')

      let promoted: RouteQueryKey | undefined
      for (const {queryName, from, keyFor} of routeQueryRequests(queries, requestSearchParams)) {
        const to = keyFor(destination)
        // A request that reads `page` asks for page one at the destination, not the page on screen.
        if (hashKey(to) !== hashKey(keyFor(unchanged))) continue

        if (queryName === 'mainQuery') {
          copyRouteQueryData(queryClient, from, to, savedConfiguration, response.savedView)
          promoted = to
        } else {
          copyRouteQueryData(queryClient, from, to)
        }
      }
      evictPrefetches(promoted)

      const query = destination.toString()
      navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true})
    },
    [evictPrefetches, navigate, pathname, queries, queryClient, requestSearchParams, search],
  )

  const onEditSuccess = useCallback(
    (
      response: UpdateSavedViewResponse<ViewConfiguration>,
      sent: UpdateSavedViewVariables<ViewConfiguration>,
      servedQuery: string,
    ) => {
      evictPrefetches()
      const {current, destination} = navigationRef.current
      if (destination && destination.pathname !== current.pathname) return true

      const next = new URLSearchParams((destination ?? current).search)
      // A pick still loading differs from what the edit saved, so it keeps its parameters.
      const clearsConfiguration = sent.configuration !== undefined && !destination
      const savedConfiguration = isViewConfiguration(response.configuration)
        ? response.configuration
        : sent.configuration
      const savedParams = clearsConfiguration ? SAVED_VIEW_URL_PARAMS.filter(param => next.has(param)) : []

      let reloads = false
      if (next.has('q') || savedParams.length > 0) {
        next.delete('q')
        for (const param of savedParams) next.delete(param)
        const withPage = new URLSearchParams(next)
        next.delete('page')
        const showsSaved = !destination && sent.query.trim() === servedQuery.trim()
        let keepsMain = false
        for (const {queryName, from, keyFor} of routeQueryRequests(queriesRef.current, requestSearchParams)) {
          const to = keyFor(next)
          const isMain = queryName === 'mainQuery'
          const configuration = isMain && clearsConfiguration ? savedConfiguration : undefined
          const savedView = isMain ? response.savedView : undefined
          if (hashKey(to) === hashKey(from)) {
            // Only visible fields left the URL, so the response on screen takes what the edit saved.
            if (showsSaved && configuration) {
              copyRouteQueryData(queryClient, from, from, configuration, savedView)
              keepsMain = true
            }
            continue
          }
          // A request that reads `page` asks for page one at the destination, not the page on screen.
          if (showsSaved && hashKey(to) === hashKey(keyFor(withPage))) {
            copyRouteQueryData(queryClient, from, to, configuration, savedView)
            if (isMain) keepsMain = true
            continue
          }
          // A response cached for the destination predates the edit, and would draw the view as it
          // was before it while the destination reloads.
          queryClient.removeQueries({queryKey: to, exact: true})
          if (isMain) reloads = true
          // Keeps the saved layout from reading as a layout change while the destination loads.
          if (configuration) copyRouteQueryData(queryClient, from, from, configuration, savedView)
        }
        const query = next.toString()
        navigate(query ? `${current.pathname}?${query}` : current.pathname, {preventAutofocus: true})
        if (keepsMain) return true
      }
      if (reloads) return true
      if (!clearsConfiguration) return false

      // Otherwise the stored configuration on screen stays the one from before the edit.
      for (const routeQuery of Object.values(queriesRef.current ?? {})) {
        if (routeQuery) void queryClient.invalidateQueries({queryKey: routeQuery.queryKey})
      }
      return true
    },
    [evictPrefetches, navigate, queryClient, requestSearchParams],
  )

  // The edit form's configuration fields write the URL as the page's own options do, so a cancelled
  // edit would otherwise keep a layout or arrangement picked in it.
  const [editStart, setEditStart] = useState<EditStart | null>(null)
  const onEditStart = useCallback(() => {
    const {current, destination} = navigationRef.current
    const from = destination ?? current
    setEditStart({
      pathname: from.pathname,
      search: new URLSearchParams(from.search),
      configuration: shownConfiguration,
    })
  }, [shownConfiguration])
  const onEditCancel = useCallback(() => {
    setEditStart(null)
    if (!editStart || !staysOnView(editStart.pathname, navigationRef.current)) return

    const {current, destination} = navigationRef.current
    const shown = new URLSearchParams((destination ?? current).search)
    const restored = restoreSavedViewOverrides(shown, editStart.search)
    if (restored.toString() === shown.toString()) return

    const query = restored.toString()
    // Replaced rather than pushed, so Back does not return to the options the edit cancelled.
    navigate(query ? `${current.pathname}?${query}` : current.pathname, {preventAutofocus: true, replace: true})
  }, [editStart, navigate])

  const editConfigurationChanged =
    editStart !== null &&
    staysOnView(editStart.pathname, navigation) &&
    editStart.configuration !== undefined &&
    shownConfiguration !== undefined &&
    Object.values(getConfigurationDirtyState(editStart.configuration, shownConfiguration)).some(Boolean)

  return {onSaveSuccess, onEditSuccess, onEditStart, onEditCancel, editConfigurationChanged}
}
